import { useEffect, useMemo, useState } from "react";
import {
  ApiPermissions,
  AssetItemListProps,
  FEATURE_FLAGS,
  MarketPair,
  METRICS_EVENTS,
  OrderBookComputedValues,
  OrderSide,
  OrderType,
  TokenPrice,
  TradeForm,
  HubTradeCreateOrderBody,
  userFacingMessages,
  Any,
  CAMPAIGN_PLACEHOLDER_NAME,
} from "@cede/types";
import {
  API_ENDPOINT,
  CEDE_CEXS,
  CEDE_CEX_LABELS,
  Logger,
  QueryKeys,
  SdkErrorCodes,
  WIDGET_ROUTES,
  forgeTokenURL,
  getCexLabel,
  getErrorMessage,
  getExchangeLogo,
  orderToTransactionMapper,
  unformatNumberAsString,
} from "@cede/utils";
import { usePopularTokens } from "../api/usePopularTokens";
import { useTradeApi } from "../api/useTradeApi";
import { useTradeForm } from "../form/useTradeForm";
import { useAutoSelectTokens } from "../utils/useAutoSelectTokens";
import { useNewAccountAdded } from "../utils/useNewAccountAdded";
import { useTradeAmountComputation } from "../utils/useTradeAmountComputation";
import { useTradeFee } from "../utils/useTradeFee";
import { useTradeMinAmount } from "../utils/useTradeMinAmount";
import { useVerifyOrder } from "../utils/useVerifyOrder";
import { connectExchangeAccount, connectSpecificExchangeAccount } from "./useTradeDispatcher";
import { useTradeEventDispatcher } from "./useTradeDispatcher";
import { tradeTabs } from "./useTradeTabs";
import { useTradeTabs } from "./useTradeTabs";
import { DEBOUNCE_DELAY } from "@/constants";
import { DroplistAccounts } from "@/hooks/accounts/types";
import { useAccounts } from "@/hooks/accounts/useAccounts";
import { useApi } from "@/hooks/core/useApi";
import { useDebounceValue } from "@/hooks/core/useDebounceFn";
import { useEffectOnce } from "@/hooks/core/useEffectOnce";
import { useSupportedExchanges } from "@/hooks/core/useSupportedExchanges";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { usePrice } from "@/hooks/prices";
import { useMultipleTransactions } from "@/hooks/trading/multi-transaction/useMultipleTransactions";
import { useAccountSelection } from "@/hooks/trading/utils/useAccountSelection";
import { useHubUser } from "@/hooks/user/useHubUser";
import { AlertTypes, generateFakeTradeTransactionTradeForm } from "@/store";
import { useAlert } from "@/store/alert/alert";
import { useLoading } from "@/store/loading/loading";
import { useNavigationStore } from "@/store/navigation/navigation";
import { useSelectedTransaction } from "@/store/transaction/transaction";
import { useUnifiedTradeStore } from "@/store/unifiedTradeStore";
import { UseTradeHook, UseTradeOpts, DropListWithIconItem } from "@/types";
import { TradeErrorType } from "@/types/trade";
import { UnifiedMarketType } from "@/types/trading";
import { orderV2ToOrder } from "@/utils/exchange";
import { filterByWhitelistedPairs, ensureWhitelistedPairTokensIncluded } from "@/utils/trade/pairSymbolsFilter";
import { getSpreadIndicatorLevel } from "@/utils/trade/spreadLevel";
import { getTradePathLabel } from "@/utils/trade/tradePath";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { useSearchParams } from "next/navigation";
import { useSnackbar } from "notistack";

declare module "notistack" {
  export interface VariantOverrides {
    trade: true;
    error: {
      incorrectPermission?: boolean;
      exchangeId?: string;
    };
  }
}

const DEFAULT_MAX_TRADE_AMOUNT = "105";

export const useTrade: UseTradeHook = (opts?: UseTradeOpts) => {
  const searchParams = useSearchParams();
  const apiHelper = useApi();
  const { trackEvent } = useMetricsEvents();
  const isTokenToLocked = useFeatureFlagIsOn(FEATURE_FLAGS.IMPROVE_UX_HUB_TOKEN_TO_LOCKED);
  const isMaxTradeAmountEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.IMPROVE_UX_HUB_MAX_TRADE_AMOUNT);
  const { hubUser, auth } = useHubUser();

  // Check if URL params are controlling the market selection
  // When true, skip syncing tradeForm.market to unified store to prevent overriding URL params
  const hasSpotUrlParams = useMemo(() => {
    const type = searchParams.get("type");
    const exchange = searchParams.get("exchange");
    const tokenTo = searchParams.get("tokenTo");
    // URL params for spot: type=spot (or no type for backward compat) + exchange + tokenTo
    return (type === "spot" || (!type && exchange)) && !!exchange && !!tokenTo;
  }, [searchParams]);

  /**
   * ************************ Utils ************************
   */
  const {
    tradeForm,
    dispatchForm,
    setFocusedInput,
    focusedInput,
    resetForm,
    addMax,
    resetAmounts,
    hasSelectedBothTokens,
  } = useTradeForm();

  // we need to debounce the amountFrom to avoid multiple requests while the user is typing
  const { debouncedValue: debouncedAmountFrom, isDebouncing } = useDebounceValue(
    tradeForm.amountFrom,
    DEBOUNCE_DELAY.DEFAULT,
  );
  const config = tradeForm.initialProps;
  const { enqueueSnackbar } = useSnackbar();
  const { selectedTab: orderType, setSelectedTab: selectOrderType } = useTradeTabs();

  const slippageThreshold = config.campaignMetadata?.slippageThreshold || 1.5;

  const { verifyOrder } = useVerifyOrder();

  const {
    assetsFrom,
    assetsTo,
    assetsToAreLoading,
    underlyingMarkets,
    orderBookMetrics,
    marketRate,
    isLoadingBalances,
    isLoadingUnderlyingMarkets,
    tradePath,
    droplistDestinationTokensError,
    shouldUseTradeSquare,
    droplistDestinationTokensAreLoading,
    markets,
    isLimitOrderSupported,
  } = useTradeApi({
    selectedAccount: tradeForm.selectedAccount,
    dispatchForm,
    tokenFrom: tradeForm.tokenFrom,
    form: tradeForm,
  });
  const { popularSymbols: popularTokens } = usePopularTokens();
  const {
    fee,
    isLoading: feeIsLoading,
    preparedOrder,
  } = useTradeFee({
    tradeForm,
    debouncedAmountFrom,
    marketRate,
    shouldUseTradeSquare,
    orderType,
  });

  const { minAmount: minAmountFrom } = useTradeMinAmount({
    tradeForm,
    marketRate,
    shouldUseTradeSquare,
    orderType,
  });

  const {
    loadTransactions: loadMultipleTransactions,
    transactions,
    clearState: clearMultipleTransactions,
    isTxLoading: isMultipleTransactionsLoading,
  } = useMultipleTransactions({
    accountId: tradeForm.selectedAccount?.value || "",
    fromTokenAmount: tradeForm.amountFrom || "",
    tradePath,
  });

  const { computeAmountFrom, computeAmountTo, computeEstimatedTradePrice, amountFromIsLoading, amountToIsLoading } =
    useTradeAmountComputation({
      tradeForm,
      dispatchForm,
      marketRate,
      shouldUseTradeSquare,
      transactions,
      orderType,
    });

  const [enabledEventDispatcher, setEnabledEventDispatcher] = useState(false);

  /**
   * ************************ State ************************
   */

  // Filter source tokens when pairSymbols is configured - only show tokens that are part of whitelisted pairs
  // Also includes tokens with zero balance (marked with needsTopUp) so users can see what they need to acquire
  const filteredAssetsFrom = useMemo(() => {
    const pairSymbols = config?.campaignMetadata?.pairSymbols;
    if (!pairSymbols?.length) return assetsFrom;
    return ensureWhitelistedPairTokensIncluded(assetsFrom, pairSymbols);
  }, [assetsFrom, config?.campaignMetadata?.pairSymbols]);

  const filteredAssetsTo = useMemo(() => {
    const recommendedTokenTo = tradeForm.initialProps.recommendedToken;
    const recommendedTokenOnly = tradeForm.initialProps.recommendedTokenOnly ?? true; // Default to true for backward compatibility
    const pairSymbols = config?.campaignMetadata?.pairSymbols;
    const tokenFrom = tradeForm.tokenFrom?.value;

    // Step 1: Apply pairSymbols filtering FIRST if configured (independent of other conditions)
    // When pairSymbols is set, Trade Square is disabled and only direct trading on whitelisted pairs is allowed
    let filtered = assetsTo;

    if (pairSymbols?.length && tokenFrom) {
      filtered = filterByWhitelistedPairs(assetsTo, pairSymbols, tokenFrom);
    }

    // Step 2: If recommendedToken features are disabled, return pairSymbols-filtered results
    if (!recommendedTokenTo || !recommendedTokenOnly || !isTokenToLocked) {
      return filtered;
    }

    // Step 3: Apply recommendedToken logic on top of pairSymbols filtering
    // if the recommended token to is not the same as the token from, return the recommended token to
    // BUT: If pairSymbols filtering was applied and reduced options, respect that!
    if (tokenFrom !== recommendedTokenTo && recommendedTokenTo) {
      // If pairSymbols filtering was applied, only return recommendedToken if it's in the filtered list
      if (pairSymbols?.length) {
        const recommendedInFiltered = filtered.find((asset) => asset.value === recommendedTokenTo);
        if (recommendedInFiltered) {
          return [recommendedInFiltered];
        }
        // recommendedToken is not in whitelist, return the filtered list (may be empty)
        return filtered;
      }

      // No pairSymbols restriction - use the original logic
      let recommendedTokenToAsset = [...filtered, ...assetsFrom].find((asset) => asset.value === recommendedTokenTo);
      if (!recommendedTokenToAsset) {
        recommendedTokenToAsset = {
          label: recommendedTokenTo,
          value: recommendedTokenTo,
          img: forgeTokenURL(recommendedTokenTo),
          balance: "0",
          freeBalance: 0,
          amount: "0",
        };
      }

      return [recommendedTokenToAsset];
    }

    // if the recommended token to is the same as the token from, return the assetsTo having a common market with the recommended token
    const assetsToWithCommonMarkets = filtered.filter((asset) => {
      return markets.some(
        (market) =>
          (market.base === asset.value && market.quote === recommendedTokenTo) ||
          (market.quote === asset.value && market.base === recommendedTokenTo),
      );
    });

    if (assetsToWithCommonMarkets.length > 0) {
      return assetsToWithCommonMarkets;
    }

    // if no common market is found, return all the filtered assets
    return filtered;
  }, [
    assetsTo,
    assetsFrom,
    markets,
    config?.campaignMetadata?.pairSymbols,
    tradeForm.tokenFrom?.value,
    tradeForm.initialProps.recommendedToken,
    tradeForm.initialProps.recommendedTokenOnly,
    isTokenToLocked,
  ]);

  const getDerivedAssetsTo = () => {
    const popularAssetsTo: DropListWithIconItem[] = [];
    const allAssetsTo: DropListWithIconItem[] = [];
    filteredAssetsTo.forEach((asset: DropListWithIconItem) => {
      if (popularTokens.includes(asset.value)) {
        popularAssetsTo.push(asset);
      } else {
        allAssetsTo.push(asset);
      }
    });
    const data: Record<string, DropListWithIconItem[]> = {};
    if (popularAssetsTo.length) data.popular = popularAssetsTo;
    if (allAssetsTo.length) data.rest = allAssetsTo;
    return data;
  };

  // Split the assetsTo in 2 parts: the popular ones and the rest
  const derivedAssetsTo = useMemo(() => {
    if (isTokenToLocked && (config.campaignId || config.hteCampaignId)) {
      return {
        campaign: filteredAssetsTo,
      };
    }
    return getDerivedAssetsTo();
  }, [filteredAssetsTo, popularTokens, isTokenToLocked, config.campaignId, config.hteCampaignId]);

  const quoteToken = tradeForm.orderSide === "buy" ? tradeForm.tokenFrom : tradeForm.tokenTo;
  const price = tradeForm.orderSide === "buy" ? marketRate.ask : marketRate.bid;

  const { droplistAccounts: availableAccounts, areAccountsLoading } = useAccounts();
  const { shouldDisableCex, isFeatureSupportedByCex, isSupportedExchangesLoading } = useSupportedExchanges();
  const setTransaction = useSelectedTransaction((state) => state.setTransaction);
  const setError = useSelectedTransaction((state) => state.setError);
  const addBigPendingRequest = useLoading((state) => state.addBigPendingRequest);
  const removeBigPendingRequest = useLoading((state) => state.removeBigPendingRequest);
  const setDescription = useLoading((state) => state.setDescription);
  const { navigateTo } = useNavigationStore();
  const queryClient = useQueryClient();
  const appendAlert = useAlert((state) => state.append);
  const removeByMessage = useAlert((state) => state.removeByMessage);

  // Query for customPrices using the injected apiHelper
  const customPrices = useQuery({
    queryKey: ["customPrices"],
    queryFn: async () => {
      if (!apiHelper) {
        Logger.error("Cannot fetch customPrices: ApiHelper not available via useApi.");
        return {};
      }
      try {
        const { data } = await apiHelper.getOrThrow(API_ENDPOINT.PRICES, undefined);
        const tokens = data?.tokens;
        const mappedTokens: { [tokenSymbol: string]: TokenPrice } = {};
        tokens?.forEach((token: TokenPrice) => {
          mappedTokens[token.tokenSymbol] = token;
        });
        return mappedTokens;
      } catch (error) {
        Logger.error("Exception fetching customPrices:", error);
        return {};
      }
    },
    enabled: !!apiHelper && tradeForm.selectedAccount?.cexValue === CEDE_CEXS.MEXC,
    refetchInterval: 1000 * 60 * 5,
  });

  const { availableForTradeAccounts, availableForTradeAccountsAreLoading } = useMemo(() => {
    if (areAccountsLoading || isSupportedExchangesLoading) {
      return {
        availableForTradeAccounts: [],
        availableForTradeAccountsAreLoading: true,
      };
    }
    let accounts = availableAccounts.filter((account) =>
      isFeatureSupportedByCex(account.cexValue, orderType === OrderType.LIMIT ? "limit_trade" : "trade"),
    );

    if (config?.exchangeIds && config.exchangeIds.length > 0) {
      accounts = accounts.filter((account) => config.exchangeIds?.includes(account.cexValue));
    }

    return {
      availableForTradeAccounts: accounts,
      availableForTradeAccountsAreLoading: false,
    };
  }, [
    availableAccounts,
    orderType,
    areAccountsLoading,
    isSupportedExchangesLoading,
    config?.exchangeIds,
    config?.campaignId,
    config?.hteCampaignId,
  ]);

  /**
   * @remarks
   * The exchanges available for trading but not registered by the user
   */
  const { notConnectedAccounts } = useMemo(() => {
    if (areAccountsLoading) {
      return {
        notConnectedAccounts: [],
        notConnectedAccountsAreLoading: true,
      };
    }
    const notConnectedExchangeIds =
      config?.exchangeIds?.filter(
        (exchangeId) => !availableAccounts.some((account) => account.cexValue === exchangeId),
      ) ?? [];
    const notConnectedAccounts: DroplistAccounts = notConnectedExchangeIds.map((exchangeId) => ({
      cexValue: exchangeId,
      label: getCexLabel(exchangeId),
      permissions: [ApiPermissions.READ, ApiPermissions.TRADE],
      value: exchangeId,
      img: getExchangeLogo(exchangeId),
      isDisabled: true,
    }));
    return {
      notConnectedAccounts,
      notConnectedAccountsAreLoading: false,
    };
  }, [config?.exchangeIds, availableAccounts]);

  const setSelectedCexSpotMarket = useUnifiedTradeStore((state) => state.setSelectedCexSpotMarket);
  const [isSwitchIconHovered, setIsSwitchIconHovered] = useState(false);

  const assetBalance = useMemo(() => {
    const balance = assetsFrom.find((asset: DropListWithIconItem) => asset.value === tradeForm.tokenFrom?.value);

    return balance?.freeBalance ?? 0;
  }, [assetsFrom, tradeForm.tokenFrom]);

  let tokenFromPrice = usePrice(tradeForm.tokenFrom?.value || "", tradeForm.selectedAccount?.cexValue);
  let tokenToPrice = usePrice(tradeForm.tokenTo?.value || "", tradeForm.selectedAccount?.cexValue);

  if (tradeForm.tokenFrom?.value === "GPT") tokenFromPrice = customPrices.data?.GPT?.exchanges?.mexc || 0;
  if (tradeForm.tokenTo?.value === "GPT") tokenToPrice = customPrices.data?.GPT?.exchanges?.mexc || 0;

  const maxAmountFromCampaign = config?.campaignMetadata?.maxTradeAmountUSD?.toString() || DEFAULT_MAX_TRADE_AMOUNT;

  const calculateMaxAmountFrom = () => {
    // Special case for Bithumb: when tokenFrom is KRW, use 75% of the balance as max
    if (tradeForm.selectedAccount?.cexValue === CEDE_CEXS.BITHUMB && tradeForm.tokenFrom?.value === "KRW") {
      const MAX_KRW_BALANCE_PERCENTAGE = 0.75;
      return new Decimal(assetBalance).mul(MAX_KRW_BALANCE_PERCENTAGE).toString();
    }

    // Default case: convert campaign USD max to token amount
    if (tokenFromPrice !== 0) {
      return new Decimal(maxAmountFromCampaign).div(tokenFromPrice).toString();
    }

    return maxAmountFromCampaign;
  };

  const maxAmountFrom = calculateMaxAmountFrom();

  const warningMessage = useMemo(() => {
    if (
      tradeForm.selectedAccount &&
      !isSupportedExchangesLoading &&
      !isFeatureSupportedByCex(tradeForm.selectedAccount.cexValue, "trade")
    ) {
      return `${CEDE_CEX_LABELS[tradeForm.selectedAccount.cexValue]} will be supported soon`;
    }

    return "";
  }, [tradeForm.selectedAccount]);

  const calculateSlippage = useMemo(() => {
    if (
      orderType !== "market" ||
      !orderBookMetrics ||
      !tradeForm.amountFrom ||
      !tradeForm.amountTo ||
      !tradeForm.orderSide
    ) {
      return 0;
    }

    const amountFrom = unformatNumberAsString(tradeForm.amountFrom).toNumber();
    const amountTo = unformatNumberAsString(tradeForm.amountTo).toNumber();

    if (amountFrom <= 0 || amountTo <= 0) return 0;

    const midPrice = orderBookMetrics.midPrice;
    if (!midPrice) return 0;

    // Calculate actual executed price from the orderbook simulation result
    const actualPrice =
      tradeForm.orderSide === "sell"
        ? amountTo / amountFrom // selling: actualPrice = amountTo / amountFrom
        : amountFrom / amountTo; // buying: actualPrice = amountFrom / amountTo

    // Calculate slippage as difference between midPrice and actualPrice
    const slippage = Math.abs((midPrice - actualPrice) / midPrice) * 100;

    return slippage;
  }, [orderType, orderBookMetrics, tradeForm.amountFrom, tradeForm.amountTo, tradeForm.orderSide]);

  const isSlippageExceeded = useMemo(() => {
    return calculateSlippage > slippageThreshold;
  }, [calculateSlippage, slippageThreshold]);

  const isSubmitButtonActive = useMemo(() => {
    return (
      verifyTradeValidity(tradeForm, assetBalance, assetsFrom, minAmountFrom, maxAmountFrom, isMaxTradeAmountEnabled) &&
      verifyLimitOrderValidity() &&
      !feeIsLoading &&
      !amountToIsLoading &&
      preparedOrder !== null
    );
  }, [
    tradeForm.tokenFrom,
    tradeForm.tokenTo,
    tradeForm.amountFrom,
    tradeForm.amountTo,
    assetBalance,
    tradeForm.market,
    assetsFrom,
    minAmountFrom,
    maxAmountFrom,
    feeIsLoading,
    isMaxTradeAmountEnabled,
    config?.campaignId,
    config?.hteCampaignId,
    amountToIsLoading,
    preparedOrder,
    debouncedAmountFrom,
    orderType,
    tradeForm.limitPrice,
  ]);

  const tradeError = useMemo(() => {
    // Don't show errors while balances are loading to prevent flickering
    if (isLoadingBalances) {
      return;
    }

    // Check if balance is lower than minimum required
    if (assetBalance < Number(minAmountFrom)) {
      return TradeErrorType.INSUFFICIENT_BALANCE_FOR_MIN;
    }

    // Check if no tokens available
    if (assetsFrom.length === 0 && !isLoadingBalances && tradeForm.selectedAccount) {
      return TradeErrorType.NO_TOKENS_AVAILABLE;
    }

    if (tradeForm.amountFrom === "0") {
      return;
    }

    // Check if amount exceeds balance
    if (tradeForm.amountFrom ? unformatNumberAsString(tradeForm.amountFrom).gt(assetBalance) : false) {
      return TradeErrorType.AMOUNT_EXCEEDS_BALANCE;
    }

    // Check if amount is below minimum
    if (minAmountFrom && tradeForm.amountFrom && unformatNumberAsString(tradeForm.amountFrom).lt(minAmountFrom)) {
      return TradeErrorType.AMOUNT_BELOW_MINIMUM;
    }

    // Check if amount is above maximum
    if (
      isMaxTradeAmountEnabled &&
      maxAmountFrom &&
      tradeForm.amountFrom &&
      unformatNumberAsString(tradeForm.amountFrom).gt(maxAmountFrom)
    ) {
      return TradeErrorType.AMOUNT_ABOVE_MAXIMUM;
    }

    // Check for high slippage
    if (isSlippageExceeded) {
      return TradeErrorType.HIGH_SLIPPAGE;
    }

    return;
  }, [
    tradeForm.amountFrom,
    assetBalance,
    minAmountFrom,
    maxAmountFrom,
    isMaxTradeAmountEnabled,
    tradeForm.selectedAccount,
    isLoadingBalances,
    isSlippageExceeded,
    calculateSlippage,
    slippageThreshold,
    config?.campaignId,
    config?.hteCampaignId,
    assetsFrom.length,
  ]);

  const hasTokenTo = useMemo(
    () => assetsFrom.find((asset: DropListWithIconItem) => asset.value === tradeForm.tokenTo?.value),
    [assetsFrom, tradeForm.tokenTo?.value],
  );

  const { isSwitchable, disabledSwitchMessage } = useMemo(() => {
    if (tradeForm.tokenFrom && tradeForm.tokenTo && hasTokenTo) {
      return {
        isSwitchable: true,
        disabledSwitchMessage: "",
      };
    }

    if (hasSelectedBothTokens) {
      return {
        isSwitchable: false,
        disabledSwitchMessage: "You must select both tokens to switch.",
      };
    }

    if (tradeForm.tokenFrom && tradeForm.tokenTo && !hasTokenTo) {
      return {
        isSwitchable: false,
        disabledSwitchMessage: `You don't have ${tradeForm.tokenTo.label}`,
      };
    }

    return {
      isSwitchable: false,
      disabledSwitchMessage: "",
    };
  }, [tradeForm.tokenTo, hasTokenTo, hasSelectedBothTokens, tradeForm.tokenFrom]);

  const addCexRedirect = () => {
    if (opts?.addCex) {
      opts.addCex();
      return;
    }

    // For hub-v2, redirect to exchange connection flow
    // This was previously handled by backgroundHandler (Chrome extension)
    connectExchangeAccount();
  };

  /**
   * ************************ Public Functions ************************
   */
  const submitTrade = async () => {
    if (config?.isHub && !config?.defiAddress) {
      enqueueSnackbar("Defi address is not set. Please reconnect your wallet.", { variant: "warning" });
      return;
    }

    setDescription(null);

    if (shouldUseTradeSquare) {
      navigateTo(WIDGET_ROUTES.MULTIPLE_TRANSACTIONS);
      return;
    }

    if (!auth.identityToken || !preparedOrder || !tradeForm.selectedAccount) return;

    // Guard: re-validate min amount at submission time to catch race conditions
    if (minAmountFrom && tradeForm.amountFrom && unformatNumberAsString(tradeForm.amountFrom).lt(minAmountFrom)) {
      enqueueSnackbar("Trade amount is below the minimum required.", { variant: "warning" });
      return;
    }

    addBigPendingRequest();
    const orderParams: HubTradeCreateOrderBody = {
      accountId: Number(tradeForm.selectedAccount.value),
      campaignId: config?.campaignId || CAMPAIGN_PLACEHOLDER_NAME.TRADE_PAGE,
      hteCampaignId: config?.hteCampaignId,
      ...preparedOrder.createOrderRequest,
      orderType,
      amount: new Decimal(preparedOrder.createOrderRequest.amount),
      price:
        orderType === OrderType.LIMIT
          ? new Decimal(unformatNumberAsString(tradeForm.limitPrice))
          : new Decimal(preparedOrder.createOrderRequest.price),
    };

    const spread = orderBookMetrics?.spread;
    const spreadThreshold = config?.campaignMetadata?.spreadThresholdAlert;

    const orderBookComputedValues: OrderBookComputedValues = {
      estimatedTradePrice: parseFloat((await computeEstimatedTradePrice("amountFrom")) || "0"),
      depth2Up: orderBookMetrics?.depths?.["2"]?.up,
      depth2Down: orderBookMetrics?.depths?.["2"]?.down,
      spread,
      spreadIndicator: getSpreadIndicatorLevel(spread, spreadThreshold),
    };
    try {
      // Call createOrder API directly
      const order = orderV2ToOrder(
        await apiHelper.postAuth(auth, API_ENDPOINT.HUB_TRADE_CREATE_ORDER, {
          bodyParams: orderParams,
          headers: {
            "sac-fetch-dest": "empty",
          },
        }),
      );

      verifyOrder(order.id);

      const baseTokenFiatPrice = preparedOrder.createOrderRequest.orderSide === "buy" ? tokenToPrice : tokenFromPrice;
      const baseFiatAmount = order.amount * baseTokenFiatPrice;

      setTimeout(() => {
        config?.onTradeSubmitted?.({
          orderParams: {
            ...orderParams,
            accountId: orderParams.accountId?.toString() || "",
            pairSymbol: orderParams.pairSymbol || "",
            orderSide: orderParams.orderSide || "buy",
          } as Any,
          order,
          exchangeId: tradeForm.selectedAccount?.cexValue,
          exchangeUserId: tradeForm.selectedAccount?.exchangeUserId,
          baseFiatAmount,
          orderBookComputedValues,
        });
      }, 3000);

      setTransaction(
        orderToTransactionMapper({
          ...order,
          accountId: tradeForm.selectedAccount.value,
          exchangeId: tradeForm.selectedAccount.cexValue,
        }),
      );
      enqueueSnackbar("Successfully traded!", {
        variant: "trade",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      if (opts?.tradingUneligibleData?.invalidAccessReason) {
        trackEvent(METRICS_EVENTS.TRADE_WARNING, {
          hubUserId: hubUser?.id,
          invalidAccessReason: opts?.tradingUneligibleData?.invalidAccessReason,
          campaignId: config?.campaignId,
          exchangeId: tradeForm.selectedAccount?.cexValue,
          amount: orderParams.amount,
          price: orderParams.price,
          orderSide: orderParams.orderSide,
          orderType: orderParams.orderType,
        });

        enqueueSnackbar("This trade did not count towards your rewards.", {
          variant: "warning",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      }

      // After successful trade, swap token direction
      // (ETH→USDC becomes USDC→ETH)
      // This provides better UX than clearing everything - user can quickly swap back if needed
      switchToken();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage, error?.code);
      setTransaction(generateFakeTradeTransactionTradeForm(tradeForm));
      config?.onTradeFailed?.({
        orderParams: {
          ...orderParams,
          accountId: orderParams.accountId?.toString() || "",
          pairSymbol: orderParams.pairSymbol || "",
          orderSide: orderParams.orderSide || "buy",
        } as Any,
        exchangeId: tradeForm.selectedAccount?.cexValue,
        errorMessage,
      });
      enqueueSnackbar(`Trade failed: ${errorMessage}`, {
        variant: "error",
        incorrectPermission: error?.code === SdkErrorCodes.EXCHANGE_INVALID_PERMISSION,
        exchangeId: tradeForm.selectedAccount?.cexValue,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
    } finally {
      setDescription(null);
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ACCOUNT_BALANCES, tradeForm.selectedAccount.value],
      });
      // Invalidate spot trading history to refresh order/trade lists
      queryClient.invalidateQueries({ queryKey: ["spot-orders"] });
      queryClient.invalidateQueries({ queryKey: ["spot-trades"] });
      // Also invalidate automatic streak balance queries (they use a different query key)
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.AUTOMATIC_STREAK_BALANCE, tradeForm.selectedAccount.value],
      });
      removeBigPendingRequest();
      resetAmounts();
    }
  };

  const switchToken = () => {
    if (
      tradeForm.tokenFrom &&
      tradeForm.tokenTo &&
      assetsFrom.map((asset: DropListWithIconItem) => asset.value === tradeForm.tokenTo?.value).includes(true)
    ) {
      dispatchForm({
        field: "amountFrom",
        value: "0",
      });
      dispatchForm({
        field: "amountTo",
        value: "0",
      });
      dispatchForm({
        field: "revertToken",
      });
    }
  };

  /**
   * ************************ Private Functions ************************
   */

  /**
   * @description Get the common market between the two tokens.
   * If there is no common market, we set the noCommonMarket flag to true to enable the trade square feature.
   */
  const getTheCurrentMarket = (newMarketsTokenFrom = underlyingMarkets) => {
    // Don't set noCommonMarket if markets haven't loaded yet OR if markets were cleared during exchange switch
    if (newMarketsTokenFrom.length === 0) {
      return;
    }

    // find market from underlying markets
    let market: MarketPair | undefined;
    let orderSide: OrderSide | undefined;

    if (tradeForm.tokenFrom && tradeForm.tokenTo) {
      const tokenFromValue = tradeForm.tokenFrom.value;
      const tokenToValue = tradeForm.tokenTo.value;

      newMarketsTokenFrom.forEach((marketPair: MarketPair) => {
        const isDirectMatch = marketPair.base === tokenFromValue && marketPair.quote === tokenToValue;
        const isReverseMatch = marketPair.base === tokenToValue && marketPair.quote === tokenFromValue;

        if (isDirectMatch) {
          market = marketPair;
          orderSide = "sell";
        } else if (isReverseMatch) {
          market = marketPair;
          orderSide = "buy";
        }
      });
    }

    if (!market || !orderSide) {
      if (tradeForm.tokenFrom && tradeForm.tokenTo) {
        dispatchForm({
          field: "noCommonMarket",
          value: true,
        });
      }
      dispatchForm({
        field: "market",
        value: null,
      });
      dispatchForm({
        field: "orderSide",
        value: null,
      });
      return;
    }

    dispatchForm({
      field: "noCommonMarket",
      value: false,
    });

    dispatchForm({
      field: "market",
      value: market,
    });

    dispatchForm({
      field: "orderSide",
      value: orderSide,
    });

    // Keep unified market selector in sync for CEX/Hyperliquid spot when we resolve a direct market
    // Skip sync if URL params are controlling the market
    if (
      !hasSpotUrlParams &&
      !shouldUseTradeSquare &&
      !tradeForm.noCommonMarket &&
      tradeForm.selectedAccount?.cexValue
    ) {
      const base = (market as Any).base || (market as Any).baseAsset;
      const quote = (market as Any).quote || (market as Any).quoteAsset;
      if (base && quote) {
        const pairSymbol = (market as Any).pairSymbol || (market as Any).symbol || `${String(base)}/${String(quote)}`;
        const current = useUnifiedTradeStore.getState().selectedMarket;
        if (
          !current ||
          current.type !== UnifiedMarketType.SPOT ||
          current.exchangeId !== tradeForm.selectedAccount.cexValue ||
          current.baseAsset !== String(base) ||
          current.quoteAsset !== String(quote) ||
          current.pairSymbol !== pairSymbol
        ) {
          setSelectedCexSpotMarket(tradeForm.selectedAccount.cexValue, String(base), String(quote), pairSymbol);
        }
      }
    }
  };

  function verifyTradeSquareValidity() {
    return shouldUseTradeSquare && tradePath?.length && !isMultipleTransactionsLoading && transactions.length;
  }

  function verifyTradeValidity(
    tradeForm: TradeForm,
    assetBalance: number,
    portfolioAssets: DropListWithIconItem[] | AssetItemListProps[],
    minAmountFrom: string | undefined,
    maxAmountFrom: string | undefined,
    isMaxTradeAmountEnabled: boolean,
  ): boolean {
    return !!(
      tradeForm.tokenFrom &&
      tradeForm.tokenTo &&
      tradeForm.amountFrom &&
      tradeForm.amountFrom === debouncedAmountFrom && // check if the debounced value is up to date
      tradeForm.amountTo &&
      assetBalance &&
      (tradeForm.market || verifyTradeSquareValidity()) &&
      portfolioAssets.map((asset: DropListWithIconItem) => asset.label === tradeForm.tokenFrom?.label).includes(true) &&
      unformatNumberAsString(tradeForm.amountFrom).lte(assetBalance) &&
      unformatNumberAsString(tradeForm.amountFrom).gt(0) &&
      minAmountFrom &&
      unformatNumberAsString(tradeForm.amountFrom).gte(minAmountFrom) &&
      (isMaxTradeAmountEnabled
        ? maxAmountFrom && unformatNumberAsString(tradeForm.amountFrom).lte(maxAmountFrom)
        : true)
    );
  }

  function verifyLimitOrderValidity() {
    return orderType === OrderType.LIMIT ? tradeForm.limitPrice !== "0" : true;
  }

  /**
   * ************************ Effects ************************
   */

  useTradeEventDispatcher({ tradeForm, tradePath, enabled: enabledEventDispatcher });

  // Centralized account selection system
  const { handleNewAccountAdded, isTransitioning } = useAccountSelection({
    campaignId: config?.campaignId,
    exchangeIds: config?.exchangeIds,
    preferredExchange: config?.preferredExchange,
    keepPreviousTradeValues: config?.keepPreviousTradeValues ?? false,
    selectedAccount: tradeForm.selectedAccount,
    availableAccounts: availableForTradeAccounts,
    setSelectedAccount: (account) => dispatchForm({ field: "selectedAccount", value: account }),
    resetForm,
    resetAmounts,
    dispatchForm,
  });

  useNewAccountAdded(availableForTradeAccounts, handleNewAccountAdded, availableForTradeAccountsAreLoading);

  useAutoSelectTokens({
    assetsTo: filteredAssetsTo,
    assetsFrom: filteredAssetsFrom,
    markets,
    isTokenToLocked,
    enabled: config?.autoSelectTokens,
  });

  // Proactively resolve direct markets when tokens change (e.g., after swap) and sync unified selector
  useEffect(() => {
    if (!tradeForm.tokenFrom || !tradeForm.tokenTo) return;
    if (!tradeForm.selectedAccount?.cexValue) return;
    if (!markets.length) return;
    // If we already have a market and it's not flagged as noCommonMarket, keep it (don't flip chart on swaps)
    if (tradeForm.market && !tradeForm.noCommonMarket) return;

    const fromSymbol = tradeForm.tokenFrom.value;
    const toSymbol = tradeForm.tokenTo.value;

    let matchedMarket: MarketPair | undefined;
    let matchedOrderSide: OrderSide | undefined;

    for (const market of markets) {
      const isDirect = market.base === fromSymbol && market.quote === toSymbol;
      const isReverse = market.base === toSymbol && market.quote === fromSymbol;

      if (isDirect) {
        matchedMarket = market;
        matchedOrderSide = "sell";
        break;
      }
      if (isReverse) {
        matchedMarket = market;
        matchedOrderSide = "buy";
        break;
      }
    }

    if (!matchedMarket || !matchedOrderSide) return;

    const shouldUpdateMarket =
      !tradeForm.market ||
      tradeForm.market.pairSymbol !== matchedMarket.pairSymbol ||
      tradeForm.market.base !== matchedMarket.base ||
      tradeForm.market.quote !== matchedMarket.quote ||
      tradeForm.orderSide !== matchedOrderSide ||
      tradeForm.noCommonMarket;

    if (shouldUpdateMarket) {
      dispatchForm({ field: "market", value: matchedMarket });
      dispatchForm({ field: "orderSide", value: matchedOrderSide });
      dispatchForm({ field: "noCommonMarket", value: false });
    }

    // Sync to unified store only if URL params are not controlling the market
    if (!hasSpotUrlParams) {
      const base = (matchedMarket as Any).base || (matchedMarket as Any).baseAsset;
      const quote = (matchedMarket as Any).quote || (matchedMarket as Any).quoteAsset;
      if (base && quote) {
        const pairSymbol =
          (matchedMarket as Any).pairSymbol || (matchedMarket as Any).symbol || `${String(base)}/${String(quote)}`;
        const current = useUnifiedTradeStore.getState().selectedMarket;
        if (
          !current ||
          current.type !== UnifiedMarketType.SPOT ||
          current.exchangeId !== tradeForm.selectedAccount.cexValue ||
          current.baseAsset !== String(base) ||
          current.quoteAsset !== String(quote) ||
          current.pairSymbol !== pairSymbol
        ) {
          setSelectedCexSpotMarket(tradeForm.selectedAccount.cexValue, String(base), String(quote), pairSymbol);
        }
      }
    }
  }, [
    hasSpotUrlParams,
    tradeForm.tokenFrom?.value,
    tradeForm.tokenTo?.value,
    tradeForm.selectedAccount?.cexValue,
    markets,
    tradeForm.market,
    tradeForm.orderSide,
    tradeForm.noCommonMarket,
    dispatchForm,
    setSelectedCexSpotMarket,
  ]);

  // Load markets when account or user data changes
  // Markets are now auto-loaded via useMarkets hook in useTradeApi
  // No need for manual loadMarkets() call

  useEffect(() => {
    if (!tradeForm.tokenFrom || !tradeForm.tokenTo || !underlyingMarkets) return;
    // Don't process if markets are still loading to avoid false noCommonMarket flags
    if (isLoadingUnderlyingMarkets) {
      return;
    }

    // Avoid unnecessary calls if we already have the correct market and orderSide
    // BUT always call if markets are still loading to ensure proper state updates
    const tokenFromValue = tradeForm.tokenFrom.value;
    const tokenToValue = tradeForm.tokenTo.value;
    const currentMarket = tradeForm.market;

    if (currentMarket) {
      const isCurrentMarketValid =
        (currentMarket.base === tokenFromValue && currentMarket.quote === tokenToValue) ||
        (currentMarket.base === tokenToValue && currentMarket.quote === tokenFromValue);

      // Also check if orderSide is correct
      const expectedOrderSide =
        currentMarket.base === tokenFromValue && currentMarket.quote === tokenToValue ? "sell" : "buy";

      if (isCurrentMarketValid && tradeForm.orderSide === expectedOrderSide && !tradeForm.noCommonMarket) {
        return;
      }
    }

    getTheCurrentMarket();
  }, [
    tradeForm.tokenTo,
    underlyingMarkets,
    tradeForm.tokenFrom,
    isLoadingUnderlyingMarkets,
    tradeForm.market,
    tradeForm.orderSide,
    tradeForm.noCommonMarket,
  ]);

  // Sync unified market selector with the active CEX/Hyperliquid spot pair when user changes tokens inside the widget
  // Skip sync if URL params are controlling the market to prevent overriding URL params with persisted tradeForm state
  useEffect(() => {
    if (hasSpotUrlParams) return;
    if (!tradeForm.market || !tradeForm.selectedAccount?.cexValue) return;
    if (shouldUseTradeSquare || tradeForm.noCommonMarket) return;

    const base = (tradeForm.market as Any).base || (tradeForm.market as Any).baseAsset;
    const quote = (tradeForm.market as Any).quote || (tradeForm.market as Any).quoteAsset;
    if (!base || !quote) return;

    const pairSymbol =
      (tradeForm.market as Any).pairSymbol || (tradeForm.market as Any).symbol || `${String(base)}/${String(quote)}`;
    const current = useUnifiedTradeStore.getState().selectedMarket;
    if (
      current &&
      current.type === UnifiedMarketType.SPOT &&
      current.exchangeId === tradeForm.selectedAccount.cexValue &&
      current.baseAsset === String(base) &&
      current.quoteAsset === String(quote) &&
      current.pairSymbol === pairSymbol
    ) {
      return;
    }

    setSelectedCexSpotMarket(tradeForm.selectedAccount.cexValue, String(base), String(quote), pairSymbol);
  }, [
    hasSpotUrlParams,
    tradeForm.market,
    tradeForm.selectedAccount?.cexValue,
    tradeForm.noCommonMarket,
    shouldUseTradeSquare,
    tradeForm.tokenFrom,
    tradeForm.tokenTo,
    setSelectedCexSpotMarket,
  ]);

  useEffect(() => {
    if (!tradeForm.tokenFrom) return;
    computeAmountTo();
  }, [marketRate, tradeForm.orderSide, shouldUseTradeSquare, transactions, tradeForm.limitPrice, orderType]);

  useEffect(() => {
    // we need to update the amount to when the amount from is updated directly by the user
    // We don't want to compute the amount to if the amount from has been updated automatically due to the computeAmountFrom function
    if (focusedInput.current === "amountFrom" && tradeForm.tokenFrom) {
      computeAmountTo();
    }
  }, [tradeForm.amountFrom, orderBookMetrics]);

  useEffect(() => {
    if (focusedInput.current === "amountTo") {
      computeAmountFrom();
    }
  }, [tradeForm.amountTo, orderBookMetrics]);

  /** Trade path */

  useEffect(() => {
    if (shouldUseTradeSquare && tradeForm.tokenTo && !droplistDestinationTokensAreLoading) {
      if (tradePath && tradePath[0]) {
        removeByMessage("You will need to perform the following");
        appendAlert(
          userFacingMessages.WIDGETS.TRADE_SQUARE.PERFORM_TRADE_PATH(
            getTradePathLabel(tradePath),
            tradeForm.tokenTo?.label || "",
          ),
          AlertTypes.INFO,
        );
        return;
      }
    }
  }, [shouldUseTradeSquare, tradeForm.tokenTo, tradePath, droplistDestinationTokensAreLoading]);

  useEffect(() => {
    if (droplistDestinationTokensError) {
      appendAlert(getErrorMessage(droplistDestinationTokensError.message), AlertTypes.ERROR);
    }
  }, [droplistDestinationTokensError]);

  /**
   * Compute the transactions each time the trade path or the amount from are updated
   * It will be used to compute the amountTo and the fees
   */
  useEffect(() => {
    if (!shouldUseTradeSquare) {
      if (transactions.length) {
        clearMultipleTransactions();
      }
      return;
    }

    if (!tradePath || tradeForm.amountFrom === "0") {
      return;
    }

    loadMultipleTransactions();
  }, [tradePath, debouncedAmountFrom, shouldUseTradeSquare]);

  useEffect(() => {
    if (!shouldUseTradeSquare || !tradePath || tradeForm.amountFrom === "0") {
      return;
    }

    if (!transactions.length && !isMultipleTransactionsLoading) {
      loadMultipleTransactions();
    }
  }, [transactions.length, isMultipleTransactionsLoading, shouldUseTradeSquare, tradePath, tradeForm.amountFrom]);

  useEffect(() => {
    // we need to clear the existing transactions in the store when the trade page is mounted
    clearMultipleTransactions();
    setEnabledEventDispatcher(true);
  }, []);

  // Auto switch to market order if the user doesn't have any available account to trade on limit
  useEffectOnce(
    () => {
      if (availableForTradeAccounts.length === 0 && orderType === OrderType.LIMIT) {
        selectOrderType(OrderType.MARKET);
      }
    },
    () => !!availableAccounts.length && !availableForTradeAccountsAreLoading,
    [availableAccounts],
    [],
  );

  useEffect(() => {
    if (isLimitOrderSupported === false) {
      selectOrderType(OrderType.MARKET);
    }
  }, [isLimitOrderSupported]);

  /**
   * @remarks
   * Reset the form when the selected account is not available
   */
  useEffect(() => {
    if (!tradeForm.selectedAccount && availableForTradeAccounts.length === 0) {
      resetForm(false);
    }
  }, [tradeForm.selectedAccount, availableForTradeAccounts.length]);

  /**
   * ************************ Return ************************
   */
  return {
    tabs: tradeTabs,
    orderType,
    selectOrderType,
    availableAccounts: availableForTradeAccounts,
    notConnectedAccounts,
    portfolioAssets: filteredAssetsFrom,
    tradeAbleAsset: derivedAssetsTo,
    tradeForm,
    tradePath,
    dispatchForm,
    tokenFromPrice,
    tokenToPrice,
    marketRate,
    switchToken,
    isSwitchable,
    isSwitchIconHovered,
    setIsSwitchIconHovered,
    disabledSwitchMessage,
    assetToLoading: shouldUseTradeSquare
      ? assetsToAreLoading || droplistDestinationTokensAreLoading
      : isLoadingUnderlyingMarkets,
    isSubmitButtonActive,
    cexFeeIsLoading: feeIsLoading || amountFromIsLoading || isDebouncing,
    minAmountFrom,
    maxAmountFrom: maxAmountFrom.toString(),
    addMax: () => addMax(assetBalance),
    amountToIsLoading: amountToIsLoading || (shouldUseTradeSquare && isMultipleTransactionsLoading),
    amountFromIsLoading,
    assetBalance,
    warningMessage,
    computeEstimatedTradePrice,
    addCexRedirect,
    submitTrade,
    setFocusedInput,
    fee,
    tradeError,
    isLoadingBalances,
    isLoadingUnderlyingMarkets,
    shouldDisableCex: shouldDisableCex("trade"),
    quoteToken,
    price,
    slippage: calculateSlippage,
    /**
     *  TODO: implement the reverse computation of the multiple transactions to allow the user to update the amountTo
     * when the trade square feature is active
     */
    allowUserInputAmountTo: !shouldUseTradeSquare,
    isMultipleTransactionsTrade: shouldUseTradeSquare,
    isLimitOrderSupported,
    config,
    orderBookMetrics,
    connectSpecificExchangeAccount,
    connectExchangeAccount,
    isAccountTransitioning: isTransitioning,
  };
};
